/*
 * Stratus — Collection Grid behaviour (st-collection-grid.js). Loaded deferred after Palo Alto's filters.js by
 * sections/st-collection-grid.liquid. filters.js stays unchanged and does the work (form → ?section_id= fetch →
 * swap of the drawer, the products and the count); this file adds what the Stratus drawer needs on the
 * <collection-filters> instance:
 *   - the drawer opens on every width (stock only shows it below 768) and locks the page while open, also
 *     across a filter request (stock unlocks the page when a request finishes);
 *   - applied-filter chips, Clear all and the empty state's button re-render the section instead of reloading;
 *   - the Filter button shows " · N" and its aria-expanded, and gets focus back when the drawer closes;
 *   - focus and scroll position inside the drawer survive the re-render;
 *   - a ?view= alternate template is kept on every request (theme previews).
 */
(function () {
  'use strict';

  var VISIBLE = 'collection__filters--visible';

  function withView(params) {
    var view = new URLSearchParams(window.location.search).get('view');
    if (!view) return params;
    var search = new URLSearchParams(params || '');
    if (!search.has('view')) search.set('view', view);
    return search.toString();
  }

  function enhance(el) {
    if (el.stEnhanced || !el.filters || !el.container) return;
    el.stEnhanced = true;

    var container = el.container;
    var returnFocus = null;
    var unlockPending = false;

    function isOpen() {
      return el.filters.classList.contains(VISIBLE);
    }

    function lockPage() {
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {
        bubbles: true,
        detail: el.filters.querySelector('[data-collection-filters-list]')
      }));
    }

    function toggles() {
      return container.querySelectorAll('[data-toggle-filters]');
    }

    function syncCount() {
      var form = el.filters.querySelector('[data-st-active-count]');
      var count = form ? parseInt(form.getAttribute('data-st-active-count'), 10) || 0 : 0;
      container.querySelectorAll('[data-st-filter-count]').forEach(function (node) {
        node.textContent = count > 0 ? ' · ' + count : '';
      });
    }

    var originalShow = el.showFiltersDrawer;
    el.showFiltersDrawer = function () {
      returnFocus = document.activeElement;
      el.filters.classList.add(VISIBLE);
      originalShow(); // sets the trigger, traps focus, calls mobileFiltersScrollLock below
    };

    el.mobileFiltersScrollLock = function () {
      if (isOpen()) lockPage();
    };

    var originalFinish = el.finishLoading.bind(el);
    el.finishLoading = function () {
      originalFinish();
      if (isOpen()) {
        lockPage();
      } else if (unlockPending) {
        unlockPending = false;
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true, detail: 300 }));
      }
    };

    var originalRenderSection = el.renderSection.bind(el);
    el.renderSection = function (params, event, updateURL) {
      return originalRenderSection(withView(params), event, updateURL !== false);
    };

    var originalRenderFilters = el.renderFilters.bind(el);
    el.renderFilters = function (html, event) {
      var active = document.activeElement;
      var focusId = active && el.filters.contains(active) ? active.id : '';
      var list = el.filters.querySelector('[data-collection-filters-list]');
      var scrollTop = list ? list.scrollTop : 0;

      originalRenderFilters(html, event);
      syncCount();

      var newList = el.filters.querySelector('[data-collection-filters-list]');
      if (newList) newList.scrollTop = scrollTop;
      if (isOpen()) {
        var target = focusId ? document.getElementById(focusId) : null;
        el.a11y.trapFocus({ container: el.filters, elementToFocus: target || undefined });
      }
    };

    // aria-expanded, the open class on <body> and focus return follow the drawer's class, whichever code path
    // (close button, scrim, Escape, resize) closed it.
    var wasOpen = isOpen();
    new MutationObserver(function () {
      var open = isOpen();
      if (open === wasOpen) return;
      wasOpen = open;
      toggles().forEach(function (button) {
        button.setAttribute('aria-expanded', String(open));
      });
      document.body.classList.toggle('st-filters-open', open);
      if (!open) {
        if (container.classList.contains('is-loading')) unlockPending = true;
        var target = returnFocus && document.body.contains(returnFocus) ? returnFocus : toggles()[0];
        returnFocus = null;
        if (target) target.focus({ preventScroll: true });
        // A lock from a request that finished as the drawer closed must not outlive it.
        window.setTimeout(function () {
          var body = document.body.classList;
          if (isOpen() || container.classList.contains('is-loading')) return;
          if (body.contains('js-drawer-open') || body.contains('js-drawer-open-cart')) return;
          if (document.documentElement.hasAttribute('data-scroll-locked')) {
            document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
          }
        }, 600);
      }
    }).observe(el.filters, { attributes: true, attributeFilter: ['class'] });

    // Chips, Clear all (toolbar row, drawer footer) and the empty state's button: re-render, don't reload.
    container.addEventListener('click', function (event) {
      var link = event.target.closest('a[data-st-filter-link]');
      if (!link || !container.contains(link)) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      var url = new URL(link.href, window.location.href);
      url.searchParams.delete('page');
      el.renderSection(url.searchParams.toString());
    });

    syncCount();
  }

  function init(root) {
    (root || document).querySelectorAll('collection-filters.st-filters').forEach(enhance);
  }

  if (window.customElements && customElements.get('collection-filters')) {
    init();
  } else if (window.customElements) {
    customElements.whenDefined('collection-filters').then(function () {
      init();
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    window.requestAnimationFrame(function () {
      init(event.target);
    });
  });
})();
